import React, { useState, useEffect } from 'react';
import { Shield, Users } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface Role {
  id: number;
  name: string;
  description: string;
  user_count: string;
  permissions?: Permission[];
}

interface Permission {
  id: number;
  name: string;
  resource: string;
  action: string;
  description: string;
}

const RoleManager: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRoles();
    fetchAllPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/roles');
      if (response.data.success) {
        setRoles(response.data.roles || []);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки ролей');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPermissions = async () => {
    try {
      const response = await api.get('/admin/roles/permissions/all');
      if (response.data.success) {
        setAllPermissions(response.data.permissions || []);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки разрешений:', error);
    }
  };

  const fetchRoleDetails = async (roleId: number) => {
    try {
      const response = await api.get(`/admin/roles/${roleId}`);
      if (response.data.success) {
        setSelectedRole(response.data.role);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки роли');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            Роли
          </h2>
          <div className="space-y-2">
            {roles.map((role) => (
              <button
                key={role.id}
                onClick={() => fetchRoleDetails(role.id)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                  selectedRole?.id === role.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900 capitalize">{role.name}</div>
                    <div className="text-sm text-gray-500">{role.description}</div>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Users className="h-4 w-4 mr-1" />
                    {role.user_count}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        {selectedRole ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 capitalize">
              {selectedRole.name} - Разрешения
            </h3>
            <p className="text-gray-600 mb-6">{selectedRole.description}</p>

            <div className="space-y-4">
              {allPermissions
                .filter(p => selectedRole.permissions?.some(sp => sp.id === p.id))
                .map((permission) => (
                  <div
                    key={permission.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{permission.name}</div>
                        <div className="text-sm text-gray-500">{permission.description}</div>
                        <div className="text-xs text-gray-400 mt-1">
                          {permission.resource} / {permission.action}
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Разрешено
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
            Выберите роль для просмотра разрешений
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManager;

